import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'src/components/Alert';
import { Button } from 'src/components/Button';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { PaymentMethodForm } from 'src/components/PaymentMethodForm';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import {
  remindSelfServePaymentCollection,
} from 'src/models/user/user';
import { isTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

type FlowStep = 'remind' | 'banner' | 'addPayment' | null;

export const SelfServePaymentCollectionFlow: React.FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selfServePaymentCollection',
  });
  const { session, user, refreshUser, updateCurrentUser } =
    useOptionalSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const ccToken = searchParams.get('self_serve_payment_collection');
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [remindDismissed, setRemindDismissed] = useState(false);

  const featureEnabled =
    session != null &&
    hasFeatureFlag(session, FeatureFlag.SelfServePaymentCollection);

  const isLinkExpired = useMemo(() => {
    if (!user?.ccExpiresAt) return false;
    return Number(user.ccExpiresAt) * 1000 < Date.now();
  }, [user?.ccExpiresAt]);

  const shouldShowFlow = useMemo(() => {
    if (!featureEnabled || !user || isTrainer(user)) return false;
    return !!user.selfServePaymentCollection;
  }, [featureEnabled, user]);

  const flowStep: FlowStep = useMemo(() => {
    if (!shouldShowFlow) return null;
    if (addPaymentOpen) return 'addPayment';
    if (ccToken) return 'addPayment';
    if (isLinkExpired && !remindDismissed) return 'remind';
    if (!user?.selfServePaymentCollectionRemindedAt && !remindDismissed) {
      return 'remind';
    }
    return 'banner';
  }, [
    addPaymentOpen,
    ccToken,
    isLinkExpired,
    remindDismissed,
    shouldShowFlow,
    user?.selfServePaymentCollectionRemindedAt,
  ]);

  useEffect(() => {
    if (isLinkExpired) {
      setRemindDismissed(false);
    }
  }, [isLinkExpired]);

  useEffect(() => {
    if (ccToken && shouldShowFlow) {
      setAddPaymentOpen(true);
    }
  }, [ccToken, shouldShowFlow]);

  const { mutate: remindLater, isPending: reminding } = useMutation({
    mutationFn: remindSelfServePaymentCollection,
    onSuccess: async () => {
      updateCurrentUser({
        selfServePaymentCollectionRemindedAt: Math.floor(Date.now() / 1000),
      });
      setRemindDismissed(true);
    },
    onError: (error: unknown) => {
      toast.error(api.formatError(error, t('remindError')));
    },
  });

  const handlePaymentMethodAdded = useCallback(
    async (result: { setupIntent?: { id?: string } }) => {
      const setupIntentId = result?.setupIntent?.id;
      if (!setupIntentId || !user) {
        toast.error(t('addPaymentError'));
        return;
      }

      try {
        await api.post('/api/v4/fp/payment_methods', {
          payment_method: {
            token: setupIntentId,
            payment_method_type: 'card',
            user_id: user.id,
            ...(ccToken ? { cc_token: ccToken } : {}),
          },
        });
        toast.success(t('addPaymentSuccess'));
        updateCurrentUser({ selfServePaymentCollection: false });
        setAddPaymentOpen(false);
        if (ccToken) {
          searchParams.delete('self_serve_payment_collection');
          setSearchParams(searchParams, { replace: true });
        }
        await refreshUser();
      } catch (error) {
        toast.error(api.formatError(error, t('addPaymentError')));
      }
    },
    [
      ccToken,
      refreshUser,
      searchParams,
      setSearchParams,
      t,
      updateCurrentUser,
      user,
    ]
  );

  if (!shouldShowFlow || !user) {
    return null;
  }

  return (
    <>
      {flowStep === 'banner' && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              variant="prominent"
              onClick={() => setAddPaymentOpen(true)}
            >
              {t('bannerCta')}
            </Button>
          }
          sx={{
            borderRadius: 0,
            alignItems: 'center',
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              py: 0.25,
            },
          }}
        >
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
            {t('bannerTitle')}
          </Typography>
          <Typography component="span" variant="body2">
            {t('bannerMessage')}
          </Typography>
        </Alert>
      )}

      <ConfirmDialog
        open={flowStep === 'remind'}
        title={t('remindTitle')}
        onClose={() => remindLater()}
        actions={[
          {
            label: t('remindMe'),
            onClick: () => remindLater(),
            color: 'neutral',
            disabled: reminding,
          },
          {
            label: t('addNow'),
            onClick: () => setAddPaymentOpen(true),
            color: 'primary',
            variant: 'prominent',
          },
        ]}
      >
        <Typography>{t('remindMessage')}</Typography>
      </ConfirmDialog>

      <Dialog
        open={flowStep === 'addPayment'}
        title={t('addPaymentTitle')}
        onClose={
          ccToken
            ? undefined
            : () => {
                setAddPaymentOpen(false);
              }
        }
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('addPaymentMessage')}
          </Typography>
          <PaymentMethodForm
            user={user}
            paymentMethodTypes={['card']}
            buttonText={t('savePaymentMethod')}
            handlePaymentMethodAdded={handlePaymentMethodAdded}
          />
        </Stack>
      </Dialog>
    </>
  );
};
